import { useEffect, useState } from 'react';

/**
 * Where you are, in about forty lines.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY NOT react-router
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The product console reached the same conclusion for the same reason: this
 * console has four screens and one nested parameter. A router brings a
 * provider, a matcher, a data API and a bundle to solve a problem that
 * `popstate` plus `history.pushState` solves in a file you can read in one
 * sitting.
 *
 * The moment there is a genuine need — nested layouts, route-level data
 * loading, blocked navigation — this is a small thing to replace, because
 * every caller uses `navigate()` and `useRoute()` and nothing reaches into
 * `window.history` directly. The test below refuses a bare `pushState`
 * anywhere else, which is what keeps that true.
 *
 * ── WHY THE URL IS THE STATE ──────────────────────────────────────────────
 *
 * A tenant page reached by clicking a row must be linkable, refreshable, and
 * reachable by the back button. Holding "which tenant" in React state instead
 * would make all three fail in the same quiet way: the page looks right until
 * somebody reloads it, and then it is the list again.
 */

export type Route =
  | { name: 'tenants' }
  | { name: 'tenant'; id: string }
  | { name: 'admins' }
  | { name: 'audit' }
  | { name: 'flags' };

/** The path this route lives at. One function, so links and matching agree. */
export function pathOf(route: Route): string {
  switch (route.name) {
    case 'tenant':
      return `/tenants/${route.id}`;
    case 'admins':
      return '/admins';
    case 'audit':
      return '/audit';
      case 'flags':
        return '/flags';
    default:
      return '/tenants';
  }
}

/**
 * The route a path names, or the tenant list.
 *
 * An unknown path lands on the list rather than on a "not found" screen. With
 * four routes and no public links, a 404 page would be a screen built for a
 * situation that only arises from a typo — and the list is a better answer to
 * a typo than an apology is.
 */
export function routeOf(path: string): Route {
  const tenant = /^\/tenants\/([^/]+)\/?$/.exec(path);
  if (tenant) return { name: 'tenant', id: decodeURIComponent(tenant[1]!) };
  if (path.startsWith('/admins')) return { name: 'admins' };
  if (path.startsWith('/audit')) return { name: 'audit' };
  if (path.startsWith('/flags')) return { name: 'flags' };
  return { name: 'tenants' };
}

/** The current route, re-read whenever the history changes. */
export function useRoute(): Route {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = (): void => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return routeOf(path);
}

/**
 * Go somewhere, and tell the listeners.
 *
 * `pushState` alone does NOT fire `popstate` — that event is for the back and
 * forward buttons. Without the dispatch below, a click would change the
 * address bar and leave the page exactly as it was, which reads as a dead
 * link rather than as a missing event.
 */
export function navigate(route: Route): void {
  const next = pathOf(route);
  if (next === window.location.pathname) return;
  window.history.pushState({}, '', next);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
